/**
 * User Management Component
 * Allows platform admins to view, edit, and delete users within an organization
 */

import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '../../config/environment';


interface User {
  userId: string;
  tenantId: string;
  email: string;
  displayName: string;
  role: 'owner' | 'admin' | 'member';
  createdAt: string;
  lastLoginAt?: string;
  invitedBy?: string;
}

interface UserManagementProps {
  tenantId: string;
  tenantName: string;
  accessToken: string;
  onClose: () => void;
}

export const UserManagement: React.FC<UserManagementProps> = ({
  tenantId,
  tenantName,
  accessToken,
  onClose
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editRole, setEditRole] = useState<'owner' | 'admin' | 'member'>('member');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    loadUsers();
  }, [tenantId]);

  const loadUsers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/tenant-admin/users/${tenantId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load users');
      }

      const data = await response.json();
      setUsers(data.users || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = (user: User) => {
    setEditingUser(user);
    setEditRole(user.role);
    setUpdateError(null);
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;

    setIsUpdating(true);
    setUpdateError(null);

    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/tenant-admin/users/${tenantId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          userId: editingUser.userId,
          role: editRole
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update user');
      }

      // Success - reload users and close modal
      await loadUsers();
      setEditingUser(null);
    } catch (err: any) {
      setUpdateError(err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteClick = (user: User) => {
    setDeletingUser(user);
    setDeleteError(null);
  };

  const handleDeleteUser = async () => {
    if (!deletingUser) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/api/tenant-admin/users/${tenantId}?userId=${deletingUser.userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete user');
      }

      // Success - reload users and close modal
      await loadUsers();
      setDeletingUser(null);
    } catch (err: any) {
      setDeleteError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const overlay  = 'fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4';
  const modal    = 'w-full max-w-3xl bg-white rounded-xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]';
  const modalHdr = 'flex items-center justify-between px-6 py-4 border-b border-gray-200';
  const thCls    = 'px-4 py-3 text-left text-meta font-semibold text-gray-500 uppercase tracking-wider';
  const tdCls    = 'px-4 py-3 text-label text-gray-700';
  const closeBtn = 'w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors duration-fast text-lg';
  const secBtn   = 'px-3 py-1.5 bg-white border border-gray-300 text-label font-medium text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-fast disabled:opacity-50';
  const priBtn   = 'px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-label font-semibold rounded-md transition-colors duration-fast disabled:opacity-50';
  const danBtn   = 'px-3 py-1.5 bg-error hover:opacity-90 text-white text-label font-semibold rounded-md transition-opacity duration-fast disabled:opacity-50';

  if (isLoading) return (
    <div className={overlay} onClick={onClose}><div className={modal} onClick={e => e.stopPropagation()}>
      <div className={modalHdr}><h2 className="text-xl font-semibold">Users in {tenantName}</h2><button type="button" onClick={onClose} className={closeBtn}>×</button></div>
      <div className="flex-1 flex items-center justify-center py-16 text-body text-gray-400">Loading users…</div>
    </div></div>
  );

  if (error) return (
    <div className={overlay} onClick={onClose}><div className={modal} onClick={e => e.stopPropagation()}>
      <div className={modalHdr}><h2 className="text-xl font-semibold">Users in {tenantName}</h2><button type="button" onClick={onClose} className={closeBtn}>×</button></div>
      <div className="flex-1 flex flex-col items-center justify-center gap-4 py-16">
        <p className="text-body text-error">Error: {error}</p>
        <button type="button" onClick={loadUsers} className={secBtn}>Retry</button>
      </div>
    </div></div>
  );

  return (
    <div className={overlay} onClick={onClose}>
      <div className={modal} onClick={e => e.stopPropagation()}>
        <div className={modalHdr}>
          <h2 className="text-xl font-semibold text-gray-900">Users in {tenantName}</h2>
          <button type="button" onClick={onClose} className={closeBtn}>×</button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {users.length === 0 ? (
            <div className="py-16 text-center text-body text-gray-400">No users found in this organization.</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                <tr>
                  <th className={thCls}>Email</th><th className={thCls}>Display Name</th>
                  <th className={thCls}>Role</th><th className={thCls}>Created</th>
                  <th className={thCls}>Last Login</th><th className={thCls}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.userId} className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-fast">
                    <td className={`${tdCls} font-mono text-meta`}>{user.email}</td>
                    <td className={tdCls}>{user.displayName}</td>
                    <td className={tdCls}>
                      <span className={`px-2.5 py-0.5 rounded-full text-meta font-semibold capitalize ${
                        user.role === 'owner' ? 'bg-blue-50 text-blue-700' : user.role === 'admin' ? 'bg-teal-500/10 text-teal-600' : 'bg-gray-100 text-gray-500'
                      }`}>{user.role}</span>
                    </td>
                    <td className={tdCls}>{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td className={tdCls}>{user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}</td>
                    <td className={tdCls}>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => handleEditClick(user)} className={secBtn}>Edit Role</button>
                        <button type="button" onClick={() => handleDeleteClick(user)} disabled={user.role === 'owner'}
                          title={user.role === 'owner' ? 'Cannot delete owner' : 'Delete user'}
                          className={danBtn}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Edit Role sub-modal */}
        {editingUser && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center p-6 z-10" onClick={() => setEditingUser(null)}>
            <div className="w-full max-w-sm bg-white rounded-xl shadow-xl p-6 flex flex-col gap-4" onClick={e => e.stopPropagation()}>
              <h3 className="text-xl font-semibold text-gray-900">Edit User Role</h3>
              {updateError && <div className="px-4 py-3 bg-error-light text-error border border-error/30 rounded-md text-label">{updateError}</div>}
              <p className="text-label text-gray-700"><strong>User:</strong> {editingUser.email}</p>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="userRole" className="text-label font-medium text-gray-700">Role</label>
                <select id="userRole" value={editRole}
                  onChange={e => setEditRole(e.target.value as 'owner' | 'admin' | 'member')}
                  disabled={isUpdating}
                  className="px-3 py-2.5 border border-gray-300 rounded-md text-body bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast select-input disabled:bg-gray-100">
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                  <option value="owner">Owner</option>
                </select>
              </div>
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => setEditingUser(null)} disabled={isUpdating} className={secBtn}>Cancel</button>
                <button type="button" onClick={handleUpdateUser} disabled={isUpdating} className={priBtn}>{isUpdating ? 'Updating…' : 'Update Role'}</button>
              </div>
            </div>
          </div>
        )}

        {/* Delete sub-modal */}
        {deletingUser && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center p-6 z-10" onClick={() => setDeletingUser(null)}>
            <div className="w-full max-w-sm bg-white rounded-xl shadow-xl p-6 flex flex-col gap-4" onClick={e => e.stopPropagation()}>
              <h3 className="text-xl font-semibold text-gray-900">Delete User</h3>
              {deleteError && <div className="px-4 py-3 bg-error-light text-error border border-error/30 rounded-md text-label">{deleteError}</div>}
              <p className="text-label text-gray-700">Are you sure you want to remove <strong>{deletingUser.email}</strong> from this organization?</p>
              <p className="text-label text-error">⚠️ This action cannot be undone. The user will lose access to all workflows and data in this organization.</p>
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => setDeletingUser(null)} disabled={isDeleting} className={secBtn}>Cancel</button>
                <button type="button" onClick={handleDeleteUser} disabled={isDeleting} className={danBtn}>{isDeleting ? 'Deleting…' : 'Delete User'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


